import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Icon } from "../components/Icon.tsx";
import { ErrorState, Loading } from "../components/Status.tsx";
import { errorText } from "../lib/errors.ts";
import { useLiveSetup, useNewLiveToken } from "../lib/live.ts";

/** Where Steam installs CS2 by default; the folder the game reads gamestate_integration_*.cfg from. */
export const CS2_CFG_FOLDER = String.raw`C:\Program Files (x86)\Steam\steamapps\common\Counter-Strike Global Offensive\game\csgo\cfg`;

/** Installing the Game State Integration file: download, drop in the cfg folder, restart the game. */
export function LiveSetupPage() {
  const { t } = useTranslation();
  const setup = useLiveSetup();
  const newToken = useNewLiveToken();
  const [copied, setCopied] = useState(false);
  const [confirmNew, setConfirmNew] = useState(false);
  const [downloaded, setDownloaded] = useState(false);

  if (setup.isPending) return <Loading />;
  if (setup.error) return <ErrorState message={errorText(t, setup.error)} onRetry={() => void setup.refetch()} />;
  const data = setup.data;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(CS2_CFG_FOLDER);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // No clipboard (plain http, old browser): the path stays selectable.
    }
  };

  return (
    <div className="page page-narrow live-setup">
      <div>
        <Link to="/live" className="back-link">
          <Icon name="chevronLeft" size={16} />
          {t("nav.live")}
        </Link>
        <h1 className="page-title">{t("liveSetup.title")}</h1>
        <p className="muted">{t("liveSetup.intro")}</p>
      </div>

      <section className={`live-banner ${data.otherAccount ? "is-warn" : data.lastSeenAt ? "is-ok" : "is-off"}`} role="status" aria-live="polite">
        <span className="live-dot" aria-hidden="true" />
        <span>
          {data.otherAccount
            ? t("live.otherAccount")
            : data.lastSeenAt
              ? t("live.lastSeen", { when: new Date(data.lastSeenAt).toLocaleString() })
              : t("liveSetup.waiting")}
        </span>
      </section>

      <ol className="live-setup-steps">
        <li className="card live-card">
          <h2 className="panel-title">{t("liveSetup.step1")}</h2>
          <p className="muted small">{t("liveSetup.step1Help")}</p>
          <a className="btn btn-primary" href="/api/live/config" download={data.fileName} onClick={() => setDownloaded(true)}>
            {t("liveSetup.download", { file: data.fileName })}
          </a>
          {downloaded && <p className="ok-text small">{t("liveSetup.downloaded")}</p>}
        </li>
        <li className="card live-card">
          <h2 className="panel-title">{t("liveSetup.step2")}</h2>
          <p className="muted small">{t("liveSetup.step2Help")}</p>
          <div className="live-path">
            <code>{CS2_CFG_FOLDER}</code>
            <button type="button" className="btn btn-secondary btn-small" onClick={() => void copy()}>
              {copied ? t("liveSetup.copied") : t("liveSetup.copy")}
            </button>
          </div>
          <p className="muted small">{t("liveSetup.step2Other")}</p>
          <p className="muted small">{t("liveSetup.oldTest")}</p>
        </li>
        <li className="card live-card">
          <h2 className="panel-title">{t("liveSetup.step3")}</h2>
          <p className="muted small">{t("liveSetup.step3Help")}</p>
        </li>
        <li className="card live-card">
          <h2 className="panel-title">{t("liveSetup.step4")}</h2>
          <p className="muted small">{t("liveSetup.step4Help")}</p>
        </li>
      </ol>

      <section className="card live-card">
        <h2 className="panel-title">{t("liveSetup.privacyTitle")}</h2>
        <p className="muted small">{t("liveSetup.privacy")}</p>
      </section>

      <section className="card live-card">
        <h2 className="panel-title">{t("liveSetup.newKeyTitle")}</h2>
        <p className="muted small">{t("liveSetup.newKeyHelp")}</p>
        {confirmNew ? (
          <div className="row">
            <button
              type="button"
              className="btn btn-danger"
              disabled={newToken.isPending}
              onClick={() =>
                newToken.mutate(undefined, {
                  onSuccess: () => {
                    setConfirmNew(false);
                    setDownloaded(false);
                  },
                })
              }
            >
              {t("liveSetup.newKeyConfirm")}
            </button>
            <button type="button" className="btn btn-quiet" onClick={() => setConfirmNew(false)}>
              {t("common.cancel")}
            </button>
          </div>
        ) : (
          <button type="button" className="btn btn-secondary" onClick={() => setConfirmNew(true)}>
            {t("liveSetup.newKey")}
          </button>
        )}
        {newToken.isSuccess && <p className="ok-text small">{t("liveSetup.newKeyDone")}</p>}
        {newToken.error && <p className="field-error small">{errorText(t, newToken.error)}</p>}
      </section>
    </div>
  );
}
